/* eslint-disable @typescript-eslint/no-explicit-any */
// components
import { Button } from "@components/Button/Button";
// packagees
// import axios from "axios";
import { SubmitHandler, useForm } from "react-hook-form";
import { useMutation } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
// import ReCAPTCHA from "react-google-recaptcha";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
// import { notification } from "antd";
import { useAppDispatch, useAppSelector } from "@/src/app/hooks";
import { authenticate } from "@/src/features/userSlice";
import va from "@vercel/analytics";
import api from "@/src/utils";
import apiRoutes from "@/src/apiRoutes";
import { toast } from "sonner";
import AuthInput from "@/src/components/AuthInput";
import { EyeIcon } from "lucide-react";
import PasswordInput from "@/src/components/PasswordInput";

const formState = z.object({
  username: z.union([
    z
      .string()
      .min(3, { message: "Username must be a minimum of 3 characters" })
      .max(15, { message: "Username must be a maximum of 3 characters" })
      .trim(),
    z.string().email({ message: "Enter a valid email address" }),
  ]),
  password: z.string().min(1, { message: "Password is required" }),
  token: z.string().optional(),
  code: z.string().optional(),
});

const Login = () => {
  const { lastPath } = useAppSelector((state) => state.user);
  type validationSchema = z.infer<typeof formState>;

  const {
    register,
    handleSubmit,
    getValues,
    formState: errors,
  } = useForm<validationSchema>({
    resolver: zodResolver(formState),
  });
  const navigate = useNavigate();
  // const useragent = navigator.userAgent;
  // const recaptchaRef = createRef<any>();
  const dispatch = useAppDispatch();
  const ua = navigator.userAgent;

  const signin = async (input: validationSchema) => {
    const res = await api.post(apiRoutes.login, {
      username: input.username,
      password: input.password,
      ua,
    });
    return res.data;
  };

  const { isLoading, mutate } = useMutation({
    mutationFn: signin,
    onSuccess: (data) => {
      if (!data.success) {
        toast.error(data?.info);
      }
      if (data.success) {
        if (data.info) {
          va.track("login");
          dispatch(authenticate(data.info));
          navigate(lastPath ?? "/app/home");
          toast.success("Successfully logged in");
        }
        if (data.extra === "tfa") {
          // setOtp(true);
          navigate("/auth/2-fa", {
            state: { username: getValues().username, token: data?.token },
          });
        } else if (data.extra === "email") {
          navigate("/auth/confirm_otp", {
            state: { username: getValues().username, token: data?.token },
          });
        }
        // else if (data.data.account) {
        //   dispatch(authenticate(data.data.account));
        //   va.track("login");
        //   if (data.data.account.kind === "trader") {
        //     dispatch(authenticate(data.data.account));
        //     notification.success({
        //       message: "Success",
        //       description: "Successfully logged in",
        //     });
        //     if (data.data?.account?.banned) {
        //       navigate("/account/banned");
        //     } else if (data.data?.account?.suspended) {
        //       navigate("/account/suspended");
        //     } else {
        //       navigate(lastPath ?? "/app/home");
        //     }

        //     if (data.data?.account?.referred) {
        //       navigate("/app/settings/profile", { state: { resetPass: true } });
        //     }
        //   } else if (data.data.account.kind?.toLowerCase() === "moderator") {
        //     dispatch(authenticate(data.data.account));
        //     navigate("/trades");
        //   }
        // } else {
        //   if (data.data?.token || data.data?.otp) {
        //     navigate("/auth/confirm_otp", {
        //       replace: true,
        //       state: data.data.token,
        //     });
        //   }
        // }
      }
    },
    onError: (error: any) => {
      if (error?.response?.data) {
        toast.error(error?.response?.data);
      }
    },
  });

  const onSubmit: SubmitHandler<validationSchema> = async (data) => {
    // setLoading(true);
    // await recaptchaRef.current
    //   .execute()
    //   // spreading the data object then appending the result received from the captcha response to it then mutating the data
    //   .then((res: any) => {
    //     setLoading(false);
    //     if (!_.isEmpty(res)) {
    //       mutate({ ...(data as any), code: res });
    //     }
    //   });
    mutate(data);
  };

  return (
    <div className=" py-5">
      <h1 className="font-semibold text-2xl tracking-tight text-center ">
        Sign in
      </h1>
      <p className="text-sm text-center text-gray-500 mt-4 md:w-4/5 mx-auto">
        We suggest you use an incognito mode or any private window to sign in if
        this is not your device{" "}
      </p>
      <form
        autoComplete="off"
        onSubmit={handleSubmit(onSubmit)}
        className="mt-5 grid gap-3"
      >
        <AuthInput
          autoComplete="off"
          {...register("username", { required: true })}
          placeholder="eg. person@mail.com"
          type="text"
          errors={errors?.errors?.username?.message}
          label={"Username or Email"}
        />
        <PasswordInput
          {...register("password", { required: true })}
          placeholder="*********"
          right={<EyeIcon className="h-5 w-5 text-gray-500" />}
          // type={checked ? "text" : "password"}
          errors={errors?.errors?.password?.message}
          label={"Password"}
        />
        <div className="flex justify-end">
          {/* <div className="flex gap-1 mb-6">
            <input type="checkbox" onChange={() => setChecked(!checked)} />
            <span className="text-sm font-medium">Show password</span>
          </div> */}

          <Link className="text-sm text-blue-600" to="/auth/forgotpassword">
            Forgot Password?
          </Link>
        </div>
        <Button
          type={"submit"}
          loading={isLoading}
          color="indigo"
          variant={"primary"}
          className="md:py-4 py-3 rounded-xl"
        >
          Sign in
        </Button>
        <p className=" text-center text-sm ">
          Don't have an account?{" "}
          <Link to="/auth/register" className="text-blue-600">
            Sign up
          </Link>{" "}
        </p>
      </form>
    </div>
  );
};

export default Login;
